// src/pages/Mypage/InsuranceCardsPage.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { axiosInstance } from '../../utils/axios';
import PageHeader from './PageHeader';
import AlertModal from '../common/AlertModal';
import { useFormValidation } from '../../utils/useFormValidation';
import { convertHeicToJpeg, isHeicFile } from '../../utils/imageConverter';
import MypageTargetSwitcher from './MypageTargetSwitcher';
import '../common/form.scss';

const ONLINE_QUALIFICATION_URL = 'https://kdg.onshikaku.org/online-care/index.html?req_type=2&mi_code=8d83bf8fcaac6a6a3adb26897ea9bfefaac0ad6d&openExternalBrowser=1';

const DOCUMENT_TABS = [
  { key: 'insurance', label: '資格確認書' },
  { key: 'medical_aid', label: '医療証' },
];

// 新設計 (medichat_users / user_type=child) を優先。
// 旧設計 (medichat_child_profiles) のみ legacy 扱い。
// ※ user_type が 'child' なら is_legacy が欠落していても新設計とみなす。
const isLegacyChildTarget = (child) => {
  if (!child) return false;
  if (child.user_type === 'child' || child.is_legacy === false) return false;
  return child.is_legacy === true || child.user_type === 'child_profile';
};

// child_user と child_profile は別テーブルで ID が衝突しうるため、種別をキーに含める。
// 旧 `child-${id}` 形式も読み取り時に後方互換で解釈する。
const childTargetId = (child) => (
  isLegacyChildTarget(child) ? `child-profile-${child.id}` : `child-user-${child.id}`
);

const findChildByTargetId = (childProfiles, targetId) => {
  if (!targetId || typeof targetId !== 'string') return null;

  if (targetId.startsWith('child-profile-')) {
    const childId = Number(targetId.replace('child-profile-', ''));
    return childProfiles.find((child) => isLegacyChildTarget(child) && child.id === childId) || null;
  }
  if (targetId.startsWith('child-user-')) {
    const childId = Number(targetId.replace('child-user-', ''));
    return childProfiles.find((child) => !isLegacyChildTarget(child) && child.id === childId) || null;
  }
  if (targetId.startsWith('child-')) {
    const childId = Number(targetId.replace('child-', ''));
    // 旧キー: 新設計を優先して解決（ID 衝突時の誤って legacy 経路に入るのを防ぐ）
    return childProfiles.find((child) => !isLegacyChildTarget(child) && child.id === childId)
      || childProfiles.find((child) => child.id === childId)
      || null;
  }
  return null;
};

const InsuranceCardsPage = () => {
  const [loading, setLoading] = useState(true);
  const [submission, setSubmission] = useState(null);
  const [childInsSubmission, setChildInsSubmission] = useState(null);
  const [childProfiles, setChildProfiles] = useState([]);
  const [selectedTargetId, setSelectedTargetId] = useState('self');
  const [selfTab, setSelfTab] = useState('insurance');
  const [childAids, setChildAids] = useState([]);
  const [adultAids, setAdultAids] = useState([]);
  const [childTab, setChildTab] = useState('insurance');
  const [submitting, setSubmitting] = useState(false);
  const [frontFile, setFrontFile] = useState(null);
  const [backFile, setBackFile] = useState(null);
  const [childInsFront, setChildInsFront] = useState(null);
  const [childInsBack, setChildInsBack] = useState(null);
  const [alertModal, setAlertModal] = useState({ open: false, title: '', message: '' });
  const [selfOnlineQualification, setSelfOnlineQualification] = useState(null);
  const [childOnlineQualification, setChildOnlineQualification] = useState(null);

  const frontRef = useRef(null);
  const backRef = useRef(null);
  const childInsFrontRef = useRef(null);
  const childInsBackRef = useRef(null);

  // バリデーション
  const { errors, validateForm, getFieldError, clearErrors } = useFormValidation('insurance_card');

  const load = async () => {
    setLoading(true);
    try {
      const [insuranceRes, childRes, adultAidRes] = await Promise.all([
        axiosInstance.get('/api/medichat/insurance_cards'),
        axiosInstance.get('/api/medichat/child_profiles').catch(() => ({ data: { child_profiles: [] } })),
        axiosInstance.get('/api/medichat/public_medical_aids').catch(() => ({ data: { public_medical_aids: [] } })),
      ]);
      const data = insuranceRes.data;
      setSubmission(data.submission || null);
      setSelfOnlineQualification(data.online_qualification || null);
      setChildProfiles(childRes?.data?.child_profiles || []);
      setAdultAids(adultAidRes?.data?.public_medical_aids || []);
    } catch (error) {
      console.error('Insurance API error:', error);
      setSubmission(null);
      setSelfOnlineQualification(null);
      setChildProfiles([]);
      setAdultAids([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const selectedChild = useMemo(
    () => findChildByTargetId(childProfiles, selectedTargetId),
    [selectedTargetId, childProfiles]
  );

  const targets = useMemo(() => ([
    { id: 'self', label: '自分' },
    ...childProfiles.map((child) => ({
      id: childTargetId(child),
      label: child.full_name || `${child.last_name || ''} ${child.first_name || ''}`.trim(),
    })),
  ]), [childProfiles]);

  const currentAdultAid = useMemo(() => adultAids[0] || null, [adultAids]);
  const currentOnlineQualification = selectedTargetId === 'self' ? selfOnlineQualification : childOnlineQualification;

  const loadChildData = async (child) => {
    if (!child?.id) {
      setChildAids([]);
      setChildInsSubmission(null);
      setChildOnlineQualification(null);
      return;
    }

    const legacy = isLegacyChildTarget(child);
    const aidsPath = legacy
      ? `/api/medichat/child_profiles/${child.id}/child_medical_aids`
      : `/api/medichat/child_profiles/child_user/${child.id}/medical_aids`;
    const insuranceParams = legacy ? { child_profile_id: child.id } : { child_user_id: child.id };

    const [aidsRes, insRes] = await Promise.all([
      axiosInstance.get(aidsPath).catch(() => ({ data: { child_medical_aids: [] } })),
      axiosInstance.get('/api/medichat/insurance_cards', { params: insuranceParams }).catch(() => ({ data: { submission: null, online_qualification: null } })),
    ]);
    setChildAids(aidsRes?.data?.child_medical_aids || []);
    setChildInsSubmission(insRes?.data?.submission || null);
    setChildOnlineQualification(insRes?.data?.online_qualification || null);
  };

  useEffect(() => {
    if (selectedChild?.id) {
      loadChildData(selectedChild);
    } else {
      setChildInsSubmission(null);
      setChildAids([]);
    }
    setFrontFile(null);
    setBackFile(null);
    setChildInsFront(null);
    setChildInsBack(null);
  }, [selectedChild]);

  const frontPreview = useMemo(() => frontFile ? URL.createObjectURL(frontFile) : null, [frontFile]);
  const backPreview = useMemo(() => backFile ? URL.createObjectURL(backFile) : null, [backFile]);
  const childInsFrontPreview = useMemo(
    () => (childInsFront ? URL.createObjectURL(childInsFront) : null),
    [childInsFront]
  );
  const childInsBackPreview = useMemo(
    () => (childInsBack ? URL.createObjectURL(childInsBack) : null),
    [childInsBack]
  );

  useEffect(() => () => {
    if (frontPreview) URL.revokeObjectURL(frontPreview);
    if (backPreview) URL.revokeObjectURL(backPreview);
  }, [frontPreview, backPreview]);

  useEffect(() => () => {
    if (childInsFrontPreview) URL.revokeObjectURL(childInsFrontPreview);
    if (childInsBackPreview) URL.revokeObjectURL(childInsBackPreview);
  }, [childInsFrontPreview, childInsBackPreview]);

  const submit = async () => {
    if (submitting) return;
    
    // バリデーション実行
    const formData = {
      front: frontFile,
      back: backFile,
      insurance_upload: true // 特別ルール用のダミーフィールド
    };
    
    if (!validateForm(formData)) {
      // バリデーションエラーがある場合
      const errorMessages = Object.values(errors).join('\n');
      setAlertModal({ 
        open: true, 
        title: '入力エラー', 
        message: errorMessages || '表面または裏面のどちらか一方は必須です' 
      });
      return;
    }
    
    // ファイルサイズチェック
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (frontFile && frontFile.size > maxSize) {
      setAlertModal({ 
        open: true, 
        title: 'ファイルサイズエラー', 
        message: '表面画像のファイルサイズは10MB以下にしてください' 
      });
      return;
    }
    if (backFile && backFile.size > maxSize) {
      setAlertModal({ 
        open: true, 
        title: 'ファイルサイズエラー', 
        message: '裏面画像のファイルサイズは10MB以下にしてください' 
      });
      return;
    }
    
    setSubmitting(true);
    try {
      // HEIC/HEIF形式の場合はJPEGに変換（ブラウザ互換性のため）
      let processedFront = frontFile;
      let processedBack = backFile;
      
      if (frontFile && isHeicFile(frontFile)) {
        processedFront = await convertHeicToJpeg(frontFile);
      }
      if (backFile && isHeicFile(backFile)) {
        processedBack = await convertHeicToJpeg(backFile);
      }

      const fd = new FormData();
      if (processedFront) fd.append('front', processedFront);
      if (processedBack) fd.append('back', processedBack);
      await axiosInstance.post('/api/medichat/insurance_cards/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFrontFile(null); 
      setBackFile(null);
      clearErrors();
      await load();
      setAlertModal({ open: true, title: '成功', message: '資格確認書をアップロードしました' });
    } catch (e) {
      console.error('Upload error:', e);
      const errorMessage = e?.response?.data?.error || 
                          e?.response?.data?.message || 
                          e?.message || 
                          'アップロードに失敗しました';
      setAlertModal({ open: true, title: 'エラー', message: errorMessage });
    } finally { 
      setSubmitting(false); 
    }
  };

  const startOnlineQualification = async () => {
    window.open(ONLINE_QUALIFICATION_URL, '_blank', 'noopener,noreferrer');
    const params = {};
    if (selectedTargetId !== 'self' && selectedChild?.id) {
      if (isLegacyChildTarget(selectedChild)) {
        params.child_profile_id = selectedChild.id;
      } else {
        params.child_user_id = selectedChild.id;
      }
    }
    if (currentOnlineQualification?.session_id) params.session_id = currentOnlineQualification.session_id;
    try {
      const response = await axiosInstance.post('/api/medichat/insurance_cards/online_qualification/start', params);
      const next = response?.data?.online_qualification || null;
      if (selectedTargetId === 'self') {
        setSelfOnlineQualification(next);
      } else {
        setChildOnlineQualification(next);
      }
    } catch (error) {
      console.warn('failed to mark online qualification started', error);
    }
  };

  const removeSide = async (side) => {
    await axiosInstance.delete(`/api/medichat/insurance_cards/attachments/${side}`);
    await load();
  };

  const submitAdultMedicalAid = async () => {
    if (submitting || !frontFile) return;

    setSubmitting(true);
    try {
      let pFront = frontFile;
      let pBack = backFile;
      if (pFront && isHeicFile(pFront)) pFront = await convertHeicToJpeg(pFront);
      if (pBack && isHeicFile(pBack)) pBack = await convertHeicToJpeg(pBack);

      const fd = new FormData();
      fd.append('front', pFront);
      if (pBack) fd.append('back', pBack);

      await axiosInstance.post('/api/medichat/public_medical_aids/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setFrontFile(null);
      setBackFile(null);
      await load();
      setAlertModal({ open: true, title: '成功', message: '医療証を保存しました' });
    } catch (e) {
      setAlertModal({ open: true, title: 'エラー', message: e?.response?.data?.error || 'アップロードに失敗しました' });
    } finally {
      setSubmitting(false);
    }
  };

  const deleteAdultAid = async () => {
    if (!currentAdultAid?.id) return;
    setSubmitting(true);
    try {
      await axiosInstance.delete(`/api/medichat/public_medical_aids/${currentAdultAid.id}`);
      setFrontFile(null);
      setBackFile(null);
      await load();
      setAlertModal({ open: true, title: '成功', message: '医療証を削除しました' });
    } catch (e) {
      setAlertModal({ open: true, title: 'エラー', message: e?.response?.data?.error || '削除に失敗しました' });
    } finally {
      setSubmitting(false);
    }
  };

  const submitChildAll = async () => {
    if (!selectedChild?.id || submitting) return;
    const hasInsurance = childInsFront || childInsBack;
    const hasMedical = frontFile;
    if (!hasInsurance && !hasMedical) return;

    setSubmitting(true);
    try {
      // 保険証アップロード
      const legacy = isLegacyChildTarget(selectedChild);
      if (childInsFront || childInsBack) {
        let pFront = childInsFront;
        let pBack = childInsBack;
        if (pFront && isHeicFile(pFront)) pFront = await convertHeicToJpeg(pFront);
        if (pBack && isHeicFile(pBack)) pBack = await convertHeicToJpeg(pBack);
        const fd = new FormData();
        if (pFront) fd.append('front', pFront);
        if (pBack) fd.append('back', pBack);
        if (legacy) {
          fd.append('child_profile_id', selectedChild.id);
        } else {
          fd.append('child_user_id', selectedChild.id);
        }
        await axiosInstance.post('/api/medichat/insurance_cards/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }

      // 医療証アップロード
      if (frontFile) {
        let pFront = frontFile;
        let pBack = backFile;
        if (pFront && isHeicFile(pFront)) pFront = await convertHeicToJpeg(pFront);
        if (pBack && isHeicFile(pBack)) pBack = await convertHeicToJpeg(pBack);
        const fd = new FormData();
        fd.append('front', pFront);
        if (pBack) fd.append('back', pBack);
        const uploadPath = legacy
          ? `/api/medichat/child_profiles/${selectedChild.id}/child_medical_aids/upload`
          : `/api/medichat/child_profiles/child_user/${selectedChild.id}/medical_aid/upload`;
        await axiosInstance.post(uploadPath, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }

      setFrontFile(null);
      setBackFile(null);
      setChildInsFront(null);
      setChildInsBack(null);
      await loadChildData(selectedChild);
      setAlertModal({ open: true, title: '成功', message: 'お子さまの資格確認書・医療証を保存しました' });
    } catch (e) {
      setAlertModal({ open: true, title: 'エラー', message: e?.response?.data?.error || 'アップロードに失敗しました' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="medichat-container">
      <div className="mypage-container mypage-container--with-fixed-footer">
        <PageHeader title="資格確認書・医療証" />

        <div className="mypage-section" style={{marginTop:'0px',boxShadow:'none'}}>
          <MypageTargetSwitcher
            targets={targets}
            currentTargetId={selectedTargetId}
            onChange={setSelectedTargetId}
          />
          <section className="mc-mypage-online-qualification" aria-labelledby="mypage-online-qualification-title">
            <div className="mc-mypage-online-qualification__copy">
              <div id="mypage-online-qualification-title" className="mc-mypage-online-qualification__title">
                マイナ在宅受付
              </div>
              <div className="mc-mypage-online-qualification__description">
                マイナ保険証で資格確認を行う方はこちらからお進みください。
              </div>
            </div>
            <button
              type="button"
              className="mc-mypage-online-qualification__button"
              onClick={startOnlineQualification}
            >
              在宅受付Webを開く
            </button>
          </section>

          {loading ? (
            <div className="mc-mypage-ins-loading">読み込み中...</div>
          ) : selectedTargetId === 'self' ? (
            <>
              <div className="mc-docs-tabs" style={{ marginBottom: '16px' }}>
                {DOCUMENT_TABS.map((tab) => (
                  <button
                    key={tab.key}
                    className={`mc-docs-tabs__item ${selfTab === tab.key ? 'mc-docs-tabs__item--active' : ''}`}
                    onClick={() => setSelfTab(tab.key)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {selfTab === 'insurance' ? (
                <>
                  <div className="mc-mypage-ins-grid">
                    <div className="mc-mypage-ins-card">
                      <div className="mc-mypage-ins-card-head">資格確認書（表面）</div>
                      <div className="mc-mypage-ins-card-body" onClick={() => frontRef.current?.click()}>
                        {frontPreview ? (
                          <>
                            <img src={frontPreview} alt="front-preview" className="mc-mypage-ins-img" />
                            <button className="mc-mypage-ins-remove" onClick={(e) => { e.stopPropagation(); setFrontFile(null); }}>×</button>
                          </>
                        ) : submission?.front_url ? (
                          <>
                            <img src={submission.front_url} alt="front" className="mc-mypage-ins-img" />
                            <button className="mc-mypage-ins-remove" onClick={(e) => { e.stopPropagation(); removeSide('front'); }}>×</button>
                          </>
                        ) : (
                          <div className="mc-mypage-ins-placeholder">
                            <div className="mc-mypage-ins-placeholder-icon">📷</div>
                            <div>表面を撮影</div>
                          </div>
                        )}
                      </div>
                      <input ref={frontRef} type="file" accept="image/*" style={{ display: 'none' }}
                            onChange={(e) => setFrontFile(e.target.files?.[0] || null)} />
                    </div>

                    <div className="mc-mypage-ins-card">
                      <div className="mc-mypage-ins-card-head">資格確認書（裏面）</div>
                      <div className="mc-mypage-ins-card-body" onClick={() => backRef.current?.click()}>
                        {backPreview ? (
                          <>
                            <img src={backPreview} alt="back-preview" className="mc-mypage-ins-img" />
                            <button className="mc-mypage-ins-remove" onClick={(e) => { e.stopPropagation(); setBackFile(null); }}>×</button>
                          </>
                        ) : submission?.back_url ? (
                          <>
                            <img src={submission.back_url} alt="back" className="mc-mypage-ins-img" />
                            <button className="mc-mypage-ins-remove" onClick={(e) => { e.stopPropagation(); removeSide('back'); }}>×</button>
                          </>
                        ) : (
                          <div className="mc-mypage-ins-placeholder">
                            <div className="mc-mypage-ins-placeholder-icon">📷</div>
                            <div>裏面を撮影</div>
                          </div>
                        )}
                      </div>
                      <input ref={backRef} type="file" accept="image/*" style={{ display: 'none' }}
                            onChange={(e) => setBackFile(e.target.files?.[0] || null)} />
                    </div>
                  </div>

                  {getFieldError('insurance_upload') && (
                    <div className="mc-form-error" style={{ margin: '1rem 0' }}>
                      {getFieldError('insurance_upload')}
                    </div>
                  )}
                </>
              ) : (
                <div>
                  <div className="mc-mypage-ins-note">
                    ご利用の場合のみ医療証をアップロードしてください。ご利用でない場合はこのまま保存せずにお戻りいただけます。
                  </div>

                  <div className="mc-mypage-ins-grid">
                    <div className="mc-mypage-ins-card">
                      <div className="mc-mypage-ins-card-head">医療証（表面）</div>
                      <div className="mc-mypage-ins-card-body" onClick={() => frontRef.current?.click()}>
                        {frontPreview ? (
                          <>
                            <img src={frontPreview} alt="医療証表" className="mc-mypage-ins-img" />
                            <button className="mc-mypage-ins-remove" onClick={(e) => { e.stopPropagation(); setFrontFile(null); }}>×</button>
                          </>
                        ) : currentAdultAid?.card_front_url ? (
                          <>
                            <img src={currentAdultAid.card_front_url} alt="医療証表" className="mc-mypage-ins-img" />
                            <button className="mc-mypage-ins-remove" onClick={(e) => { e.stopPropagation(); deleteAdultAid(); }} disabled={submitting}>×</button>
                          </>
                        ) : (
                          <div className="mc-mypage-ins-placeholder">
                            <div className="mc-mypage-ins-placeholder-icon">📷</div>
                            <div>表面を撮影</div>
                          </div>
                        )}
                      </div>
                      <input ref={frontRef} type="file" accept="image/*,.heic,.heif" style={{ display: 'none' }}
                        onChange={(e) => setFrontFile(e.target.files?.[0] || null)} />
                    </div>
                    <div className="mc-mypage-ins-card">
                      <div className="mc-mypage-ins-card-head">医療証（裏面）</div>
                      <div className="mc-mypage-ins-card-body" onClick={() => backRef.current?.click()}>
                        {backPreview ? (
                          <>
                            <img src={backPreview} alt="医療証裏" className="mc-mypage-ins-img" />
                            <button className="mc-mypage-ins-remove" onClick={(e) => { e.stopPropagation(); setBackFile(null); }}>×</button>
                          </>
                        ) : currentAdultAid?.card_back_url ? (
                          <>
                            <img src={currentAdultAid.card_back_url} alt="医療証裏" className="mc-mypage-ins-img" />
                            <button className="mc-mypage-ins-remove" onClick={(e) => { e.stopPropagation(); deleteAdultAid(); }} disabled={submitting}>×</button>
                          </>
                        ) : (
                          <div className="mc-mypage-ins-placeholder">
                            <div className="mc-mypage-ins-placeholder-icon">📷</div>
                            <div>裏面を撮影</div>
                          </div>
                        )}
                      </div>
                      <input ref={backRef} type="file" accept="image/*,.heic,.heif" style={{ display: 'none' }}
                        onChange={(e) => setBackFile(e.target.files?.[0] || null)} />
                    </div>
                  </div>

                  {currentAdultAid && (
                    <div className="mc-mypage-ins-hint">
                      変更する場合は新しい写真を選んで保存してください。削除する場合は画像右上の「×」を押してください。
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <div>
              {/* タブ切り替え */}
              <div className="mc-docs-tabs" style={{ marginBottom: '16px' }}>
                {DOCUMENT_TABS.map((tab) => (
                  <button
                    key={tab.key}
                    className={`mc-docs-tabs__item ${childTab === tab.key ? 'mc-docs-tabs__item--active' : ''}`}
                    onClick={() => setChildTab(tab.key)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* === 保険証タブ === */}
              {childTab === 'insurance' && (
                <div>
                  <div className="mc-mypage-ins-grid">
                    <div className="mc-mypage-ins-card">
                      <div className="mc-mypage-ins-card-head">資格確認書（表面）</div>
                      <div className="mc-mypage-ins-card-body" onClick={() => childInsFrontRef.current?.click()}>
                        {childInsFrontPreview ? (
                          <>
                            <img src={childInsFrontPreview} alt="保険証表" className="mc-mypage-ins-img" />
                            <button className="mc-mypage-ins-remove" onClick={(e) => { e.stopPropagation(); setChildInsFront(null); }}>×</button>
                          </>
                        ) : childInsSubmission?.front_url ? (
                          <>
                            <img src={childInsSubmission.front_url} alt="保険証表" className="mc-mypage-ins-img" />
                          </>
                        ) : (
                          <div className="mc-mypage-ins-placeholder">
                            <div className="mc-mypage-ins-placeholder-icon">📷</div>
                            <div>表面を撮影</div>
                          </div>
                        )}
                      </div>
                      <input ref={childInsFrontRef} type="file" accept="image/*,.heic,.heif" style={{ display: 'none' }}
                        onChange={(e) => setChildInsFront(e.target.files?.[0] || null)} />
                    </div>
                    <div className="mc-mypage-ins-card">
                      <div className="mc-mypage-ins-card-head">資格確認書（裏面）</div>
                      <div className="mc-mypage-ins-card-body" onClick={() => childInsBackRef.current?.click()}>
                        {childInsBackPreview ? (
                          <>
                            <img src={childInsBackPreview} alt="保険証裏" className="mc-mypage-ins-img" />
                            <button className="mc-mypage-ins-remove" onClick={(e) => { e.stopPropagation(); setChildInsBack(null); }}>×</button>
                          </>
                        ) : childInsSubmission?.back_url ? (
                          <>
                            <img src={childInsSubmission.back_url} alt="保険証裏" className="mc-mypage-ins-img" />
                          </>
                        ) : (
                          <div className="mc-mypage-ins-placeholder">
                            <div className="mc-mypage-ins-placeholder-icon">📷</div>
                            <div>裏面を撮影</div>
                          </div>
                        )}
                      </div>
                      <input ref={childInsBackRef} type="file" accept="image/*,.heic,.heif" style={{ display: 'none' }}
                        onChange={(e) => setChildInsBack(e.target.files?.[0] || null)} />
                    </div>
                  </div>
                </div>
              )}

              {/* === 医療証タブ === */}
              {childTab === 'medical_aid' && (
                <div>
                  <div className="mc-mypage-ins-grid">
                    <div className="mc-mypage-ins-card">
                      <div className="mc-mypage-ins-card-head">医療証（表面）</div>
                      <div className="mc-mypage-ins-card-body" onClick={() => frontRef.current?.click()}>
                        {frontPreview ? (
                          <>
                            <img src={frontPreview} alt="医療証表" className="mc-mypage-ins-img" />
                            <button className="mc-mypage-ins-remove" onClick={(e) => { e.stopPropagation(); setFrontFile(null); }}>×</button>
                          </>
                        ) : childAids[0]?.card_front_url ? (
                          <>
                            <img src={childAids[0].card_front_url} alt="医療証表" className="mc-mypage-ins-img" />
                          </>
                        ) : (
                          <div className="mc-mypage-ins-placeholder">
                            <div className="mc-mypage-ins-placeholder-icon">📷</div>
                            <div>表面を撮影</div>
                          </div>
                        )}
                      </div>
                      <input ref={frontRef} type="file" accept="image/*,.heic,.heif" style={{ display: 'none' }}
                        onChange={(e) => setFrontFile(e.target.files?.[0] || null)} />
                    </div>
                    <div className="mc-mypage-ins-card">
                      <div className="mc-mypage-ins-card-head">医療証（裏面）</div>
                      <div className="mc-mypage-ins-card-body" onClick={() => backRef.current?.click()}>
                        {backPreview ? (
                          <>
                            <img src={backPreview} alt="医療証裏" className="mc-mypage-ins-img" />
                            <button className="mc-mypage-ins-remove" onClick={(e) => { e.stopPropagation(); setBackFile(null); }}>×</button>
                          </>
                        ) : childAids[0]?.card_back_url ? (
                          <>
                            <img src={childAids[0].card_back_url} alt="医療証裏" className="mc-mypage-ins-img" />
                          </>
                        ) : (
                          <div className="mc-mypage-ins-placeholder">
                            <div className="mc-mypage-ins-placeholder-icon">📷</div>
                            <div>裏面を撮影</div>
                          </div>
                        )}
                      </div>
                      <input ref={backRef} type="file" accept="image/*,.heic,.heif" style={{ display: 'none' }}
                        onChange={(e) => setBackFile(e.target.files?.[0] || null)} />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="mc-fixed-footer-spacer" />
        </div>
      </div>

      <div className="mc-fixed-footer">
        <button 
          className="mc-mypage-ins-submitbtn mc-fixed-footer__btn" 
          disabled={
            submitting || (
              selectedTargetId === 'self'
                ? (selfTab === 'insurance' ? (!frontFile && !backFile) : !frontFile)
                : (!childInsFront && !childInsBack && !frontFile && !backFile)
            )
          }
          onClick={selectedTargetId === 'self' ? (selfTab === 'insurance' ? submit : submitAdultMedicalAid) : submitChildAll}
        >
          {submitting ? '送信中...' : '保存する'}
        </button>
      </div>
      
      <AlertModal 
        open={alertModal.open}
        title={alertModal.title}
        message={alertModal.message}
        onClose={() => setAlertModal({ open: false, title: '', message: '' })}
      />
    </div>
  );
};

export default InsuranceCardsPage;
